import { useNavigate, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import {
    useAddContactMutation,
    useEditContactMutation,
    useGetBusinessCardDetailsMutation,
    useGetContactQuery
} from '../../api/contact.js';
import { useGetCountriesQuery, useGetPoliticalAffiliationsQuery, useGetSkillsQuery } from '../../api/misc.js';
import { CONTACT_SCHEMA } from '../../utils/contact.js';
import { useErrors } from '../../hooks/useErrors.js';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import ContactCompanies from '../../components/misc/ContactCompanies.jsx';
import SocialLinks from '../../components/misc/SocialLinks.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import BaseRating from '../../components/form/BaseRating.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import Notes from '../../components/misc/Notes.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import IconClose from '../../components/images/IconClose.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { useSelector } from 'react-redux';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import { PERMISSION_CONTACTS, PERMISSION_FILES } from '../../utils/constants.js';
import ContactDeleteConfirmation from '../../components/misc/ContactDeleteConfirmation.jsx';
import { Accordion, AccordionTab } from 'primereact/accordion';
import AssociateFiles from '../../components/misc/AssociateFiles.jsx';
import BaseSwitch from '../../components/form/BaseSwitch.jsx';
import BaseRadio from '../../components/form/BaseRadio.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import BaseTreeSelect from '../../components/form/BaseTreeSelect.jsx';
import { addTreeSelection, findItem, removeAttribute } from '../../utils/helper.js';
import { Badge } from 'primereact/badge';
import BaseUpload from '../../components/form/BaseUpload.jsx';
import { useInfoToast, useSuccessToast } from '../../hooks/useToast.jsx';
import { transformFilesForRequest } from '../../utils/file.js';

const AddEditContact = ({ edit = false }) => {
    let contact = null;
    const { permissions: userPermissions } = useCurrentUser();
    const [deleteVisible, setDeleteVisible] = useState(false);
    const user = useSelector((state) => state.user.user);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const { id } = useParams();

    const [hideCompanySearch, setHideCompanySearch] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [needsOrSkills, setNeedsOrSkills] = useState('');
    const [selectedSkills, setSelectedSkills] = useState('');
    const [selectedNeeds, setSelectedNeeds] = useState('');

    const [isPrivate, setIsPrivate] = useState(true);

    const [showFileSearch, setShowFileSearch] = useState(true);

    const [newsKeyword, setNewsKeyword] = useState('');

    if (edit && id) {
        const { data: contactResponse } = useGetContactQuery(id);

        contact = useMemo(() => {
            if (contactResponse?.response?.contact) {
                return contactResponse.response.contact;
            }

            return null;
        }, [contactResponse]);
    }

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;

        user.organizations.forEach((item) => {
            const permission = user.role.permissions.find(
                (value) => value.organization_id === item.id && value.permission === PERMISSION_CONTACTS
            );

            const hasPermission = permission && ((permission.create && !edit) || (permission.update && edit));

            if (hasPermission) {
                setPermittedOrganizations((prevState) => {
                    if (!prevState.some((org) => org.id === item.id)) {
                        return [...prevState, item];
                    }
                    return prevState;
                });
            }
        });
    }, [user, edit]);

    const [getBusinessCardDetails, { isLoading: isLoadingBusinessCard, error: errorBusinessCard }] =
        useGetBusinessCardDetailsMutation();

    const [addContact, { isLoading, error }] = useAddContactMutation();
    const [editContact, { isLoading: isLoadingEdit, error: errorEdit }] = useEditContactMutation();
    const navigate = useNavigate();

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    const { data: politicalAffiliationsData } = useGetPoliticalAffiliationsQuery({});
    const politicalAffiliations = useMemo(() => {
        if (politicalAffiliationsData?.response?.political_affiliations) {
            return politicalAffiliationsData.response.political_affiliations;
        }

        return [];
    }, [politicalAffiliationsData]);

    const treeElement = useCallback((key, item) => {
        return {
            key,
            label: item.name,
            data: item.id,
            ...(item.sub_skills && item.sub_skills.length > 0
                ? {
                      children: item.sub_skills.map((sub_skill, index) => {
                          return treeElement(`${key}-${index}`, sub_skill);
                      })
                  }
                : {})
        };
    }, []);

    const { data: skillsData } = useGetSkillsQuery({});
    const skills = useMemo(() => {
        let skillOptions = [];
        if (skillsData?.response?.skills) {
            let skillsArr = skillsData.response.skills;
            skillsArr.map((item, index) => {
                skillOptions.push(treeElement(index, item));
            });
        }

        return skillOptions;
    }, [skillsData, treeElement]);

    const { handleSubmit, getValues, formState, control, clearErrors, reset, setValue, watch } = useForm({
        resolver: yupResolver(CONTACT_SCHEMA),
        defaultValues: {
            first_name: '',
            middle_name: '',
            last_name: '',
            email: '',
            phone: '',
            details: {
                political_affiliation: '',
                reputation: 0
            },
            country_id: '',
            companies: !edit
                ? [
                      {
                          organization_id: '',
                          name: '',
                          work_email: '',
                          work_phone: '',
                          job_title: ''
                      }
                  ]
                : [],
            needs: [],
            skills: [],
            social_links: [],
            notes: [
                {
                    note: ''
                }
            ],
            files: [],
            shared: false,
            shared_organizations: [],
            shared_users: [],
            news_enabled: false,
            news_frequency: 'daily',
            news_keywords: []
        },
        reValidateMode: 'onSubmit'
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'social_links'
    });

    const {
        fields: fieldsCompany,
        append: appendCompany,
        remove: removeCompany,
        update: updateCompany
    } = useFieldArray({
        control,
        name: 'companies'
    });

    const {
        fields: fieldsNotes,
        append: appendNote,
        remove: removeNote
    } = useFieldArray({
        control,
        name: 'notes'
    });

    const {
        fields: fieldsNeeds,
        append: appendNeed,
        remove: removeNeed
    } = useFieldArray({
        control,
        name: 'needs'
    });

    const {
        fields: fieldsSkills,
        append: appendSkill,
        remove: removeSkill
    } = useFieldArray({
        control,
        name: 'skills'
    });

    const {
        fields: fieldsFiles,
        append: appendFile,
        remove: removeFile
    } = useFieldArray({
        control,
        name: 'files'
    });

    const closeNeedsAndSkills = () => {
        setShowOverlay(false);
        setNeedsOrSkills('');
    };

    const addNeedsOrSkills = (mode) => {
        setShowOverlay(true);
        setNeedsOrSkills(mode);
    };

    const { errors } = edit ? useErrors(errorEdit?.message, formState) : useErrors(error?.message, formState);
    const sharedOrganizations = watch('shared_organizations');
    const sharedUsers = watch('shared_users');
    const newsEnabled = watch('news_enabled');
    const newsKeywords = watch('news_keywords') || [];

    const handleSharedOrganizations = (index, value) => {
        if (permittedOrganizations.length > 0) {
            const organizationId = permittedOrganizations[index].id;

            const updatedSharedOrganizations = value
                ? [...sharedOrganizations, organizationId]
                : sharedOrganizations.filter((orgId) => orgId !== organizationId);

            setValue('shared_organizations', updatedSharedOrganizations);

            if (updatedSharedOrganizations.length === 0) {
                handleSharedUsers(true);
            }
        }
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length > 0) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const updatedSharedUsers = value ? [...sharedUsers, id] : sharedUsers.filter((id) => id !== id);

        setValue('shared_users', updatedSharedUsers);
    };

    const renderShared = () => {
        if (permittedOrganizations.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }

        return (
            <div>
                {permittedOrganizations.map(({ id, name }, index) => (
                    <SharedModelListItem
                        key={id}
                        index={index}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        isLoading={isLoading}
                        disabled={isPrivate}
                    />
                ))}

                <PrivateUsersListItem
                    key='separate-item'
                    text={'Private'}
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={contact?.shared_users || []}
                    isLoading={isLoading}
                    permission={PERMISSION_CONTACTS}
                />
            </div>
        );
    };

    const handleDeleteConfirmation = () => {
        setDeleteVisible(true);
    };

    const sendEditContact = async (requestData) => {
        const finalFiles = transformFilesForRequest(requestData.files);

        const updatedData = {
            ...requestData,
            shared: requestData.shared_organizations.length > 0,
            files: finalFiles
        };

        await editContact({ id: id || '', data: updatedData }).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/contacts/view/' + id);
            }
        });
    };

    const sendAddContact = async (requestData) => {
        const finalFiles = transformFilesForRequest(requestData.files);

        const updatedData = {
            ...requestData,
            shared: requestData.shared_organizations.length > 0,
            files: finalFiles
        };

        await addContact(updatedData).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/contacts/view/' + apiResponse.data?.response.id);
            }
        });
    };

    const extractDetailsFromFile = async (files) => {
        const businessCardFile = files.length > 0 ? files[0] : null;

        if (businessCardFile) {
            useInfoToast('Please wait, we are getting details from business card...');
            await getBusinessCardDetails({ imageFile: businessCardFile }).then((apiResponse) => {
                if (apiResponse.data?.success) {
                    const { response } = apiResponse.data;

                    reset({
                        first_name: response.first_name ?? '',
                        last_name: response.last_name ?? '',
                        companies: [
                            {
                                organization_id: response.company_id ?? '',
                                name: response.company ?? '',
                                work_email: response.email ?? '',
                                work_phone: response.phone ?? '',
                                job_title: response.job_title ?? ''
                            }
                        ],
                        middle_name: '',
                        email: '',
                        phone: '',
                        details: {
                            political_affiliation: '',
                            reputation: 0
                        },
                        country_id: '',
                        needs: [],
                        skills: [],
                        social_links: [],
                        notes: [
                            {
                                note: ''
                            }
                        ],
                        files: [],
                        shared: false,
                        shared_organizations: [],
                        shared_users: [],
                        news_enabled: false,
                        news_frequency: 'daily',
                        news_keywords: []
                    });

                    useSuccessToast('Populated details from business card successfully!');
                    if (response.company !== null || response.company_id !== null) {
                        setHideCompanySearch(true);
                    } else {
                        setHideCompanySearch(false);
                    }
                }
            });
        }
    };

    const formValues = getValues();

    const setAttribute = (treeArray, field, func, property) => {
        let arr = {};

        const processNode = (node, selectedIds) => {
            let key = findItem(node, node.data);
            if (key === undefined || key === null) return { checked: false, partialChecked: false };

            if (node.children && node.children.length > 0) {
                let childrenStates = node.children.map((child) => processNode(child, selectedIds));

                let allChildrenChecked = childrenStates.every((child) => child.checked);
                let someChildrenChecked = childrenStates.some((child) => child.checked || child.partialChecked);

                arr[key] = {
                    checked: allChildrenChecked,
                    partialChecked: someChildrenChecked && !allChildrenChecked
                };
                return arr[key];
            } else {
                let isSelected = selectedIds.includes(node.data);
                arr[key] = { checked: isSelected, partialChecked: false };
                return arr[key];
            }
        };

        let selectedIds = field.map((item) => item[property]);

        treeArray.forEach((skill) => {
            processNode(skill, selectedIds);
        });

        func(arr);
    };

    useEffect(() => {
        if (contact) {
            if (!canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'update')) {
                navigate('/unauthorized');
                return;
            }
            const newValues = {
                first_name: contact.first_name || '',
                middle_name: contact.middle_name || '',
                last_name: contact.last_name || '',
                email: contact.email || '',
                phone: contact.phone || '',
                details: {
                    political_affiliation: contact.details.political_affiliation || '',
                    reputation: contact.details.reputation || 0
                },
                country_id: contact.country_id,
                companies:
                    contact.companies.length > 0
                        ? [
                              ...contact.companies.map((company) => ({
                                  organization_id: company.organization_id || '',
                                  name: company.name || '',
                                  work_email: company.work_email || '',
                                  work_phone: company.work_phone || '',
                                  job_title: company.job_title || ''
                              }))
                          ]
                        : [
                              {
                                  organization_id: '',
                                  name: '',
                                  work_email: '',
                                  work_phone: '',
                                  job_title: ''
                              }
                          ],
                needs: contact.needs || [],
                skills: contact.skills || [],
                social_links: contact.social_links || [],
                notes:
                    contact.notes.length > 0
                        ? [
                              ...contact.notes.map((note) => ({
                                  note: note.note || ''
                              }))
                          ]
                        : [
                              {
                                  note: ''
                              }
                          ],
                shared: contact.shared,
                shared_organizations: contact.shared_organizations,
                shared_users: contact.shared_users.map((shareUser) => shareUser.id),
                files:
                    contact.files?.map((file) => ({
                        file_id: file.file_id,
                        name: file.title,
                        path: file.url,
                        extension: file.extension,
                        type: file.category
                    })) || [],
                news_enabled: contact.news_enabled || false,
                news_frequency: contact.news_frequency || 'daily',
                news_keywords: contact.news_keywords || []
            };

            reset(newValues);
            setIsPrivate(!contact.shared);
        }
    }, [contact, navigate, reset, user]);

    useEffect(() => {
        setAttribute(skills, fieldsSkills, setSelectedSkills, 'skill_id');
        setAttribute(skills, fieldsNeeds, setSelectedNeeds, 'need_id');
    }, [skills, fieldsSkills, fieldsNeeds]);

    const handleAddNewsKeyword = () => {
        const trimmed = newsKeyword.trim();
        if (trimmed !== '') {
            setValue('news_keywords', [...newsKeywords, trimmed]);
            setNewsKeyword('');
        }
    };

    return (
        <AuthLayout title={edit ? 'Edit Contact' : 'Add Contact'}>
            <div className='max-w-3xl w-full mx-auto px-8'>
                {!edit && (
                    <div className='max-w-3xl w-full mx-auto px-4 my-6'>
                        <div className='text-lg font-bold text-theme1-title2 pb-2'>Get details from business card</div>
                        <BaseUpload
                            accept={['png', 'jpg', 'jpeg', 'heic', 'heif']}
                            onFilesUpload={extractDetailsFromFile}
                            multiple={false}
                            isLoading={isLoadingBusinessCard}
                            errors={errorBusinessCard}
                        />
                    </div>
                )}
                <Accordion
                    className={`custom-accordion mt-2 ${!edit ? 'mb-8' : 'pb-2'}`}
                    multiple={false}
                    activeIndex={edit ? null : 0}
                >
                    <AccordionTab
                        key='shared-within'
                        header={
                            <span className='text-lg font-bold text-theme1-text2'>
                                Shared within {!edit && <span className='text-theme1-brightRed'>*</span>}
                            </span>
                        }
                    >
                        {renderShared()}
                    </AccordionTab>
                </Accordion>
                <div className='mb-4 text-theme1-primary font-bold text-xl'>Personal Information</div>
                <div className='mb-10'>
                    <BaseInput
                        type='text'
                        control={control}
                        label='First Name'
                        name='first_name'
                        clearErrors={clearErrors}
                        errors={[...(errors?.first_name ? errors.first_name : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Middle Name'
                        name='middle_name'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Last Name'
                        name='last_name'
                        clearErrors={clearErrors}
                        errors={[...(errors?.last_name ? errors.last_name : [])]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Email'
                        name='email'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseInput
                        className='mt-4'
                        type='text'
                        control={control}
                        label='Phone'
                        name='phone'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseDropdown
                        className='mt-4'
                        control={control}
                        label='Political View'
                        name='details.political_affiliation'
                        clearErrors={clearErrors}
                        errors={[]}
                        options={politicalAffiliations}
                    />

                    <BaseRating
                        className='mt-4'
                        control={control}
                        label='Reputation'
                        name='details.reputation'
                        clearErrors={clearErrors}
                        errors={[]}
                    />

                    <BaseDropdown
                        className='mt-4'
                        control={control}
                        label='Country'
                        name='country_id'
                        clearErrors={clearErrors}
                        errors={[]}
                        options={countries}
                    />
                </div>

                <div className='mb-10 border border-theme1-text3 p-4 rounded-2xl'>
                    <div className='text-theme1-primary font-bold text-xl mb-4'>News Settings</div>
                    <BaseSwitch
                        control={control}
                        name='news_enabled'
                        label={newsEnabled ? 'Disable news' : 'Enable news'}
                        clearErrors={clearErrors}
                        errors={[]}
                    />
                    {newsEnabled && (
                        <>
                            <div className='mt-4'>
                                <div className='text-lg font-bold text-theme1-primary mb-1'>Frequency</div>
                                <div className='flex items-center space-x-4'>
                                    <BaseRadio
                                        control={control}
                                        name='news_frequency'
                                        value='daily'
                                        textValue='Daily'
                                        clearErrors={clearErrors}
                                        errors={[]}
                                    />
                                    <BaseRadio
                                        control={control}
                                        name='news_frequency'
                                        value='weekly'
                                        textValue='Weekly'
                                        clearErrors={clearErrors}
                                        errors={[]}
                                    />
                                </div>
                            </div>
                            <div className='mt-4'>
                                <div className='text-lg font-bold text-theme1-primary mb-1'>News Keywords</div>
                                <div className='flex items-center space-x-2'>
                                    <BaseInput
                                        type='text'
                                        placeholder='Enter keyword'
                                        value={newsKeyword}
                                        setValue={setNewsKeyword}
                                        errors={[]}
                                        className='w-3/4'
                                    />
                                    <BaseButton
                                        onClick={handleAddNewsKeyword}
                                        textValue='Add'
                                        className='btn-primary w-1/4 px-2 py-3'
                                    />
                                </div>
                                <div className='flex items-center flex-wrap mt-4'>
                                    {newsKeywords.length > 0 &&
                                        newsKeywords.map((keyword, index) => (
                                            <span
                                                key={index}
                                                className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                            >
                                                <span>{keyword}</span>
                                                <span
                                                    onClick={() =>
                                                        setValue(
                                                            'news_keywords',
                                                            newsKeywords.filter((_, i) => i !== index)
                                                        )
                                                    }
                                                    className='ml-2 cursor-pointer'
                                                >
                                                    <IconClose className='w-5 h-5 fill-theme1-white' />
                                                </span>
                                            </span>
                                        ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <SocialLinks
                    addNew={fields.length === 0}
                    fields={fields}
                    control={control}
                    append={append}
                    remove={remove}
                    clearErrors={clearErrors}
                />

                <ContactCompanies
                    hideSearch={hideCompanySearch}
                    fields={fieldsCompany}
                    control={control}
                    append={appendCompany}
                    remove={removeCompany}
                    clearErrors={clearErrors}
                    update={updateCompany}
                />
            </div>
            <div className='max-w-3xl w-full mx-auto px-8 mt-8 mb-10'>
                <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
                    <div className='px-8 flex items-center justify-between p-6'>
                        <div className='text-2xl font-bold text-theme1-title2'>Needs & Skills</div>
                    </div>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion' activeIndex={edit ? null : 0}>
                                <AccordionTab
                                    key='needs-tab'
                                    className='custom-accordion-tab-no-padding'
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                            <Badge value={fieldsNeeds?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {fieldsNeeds.length > 0 &&
                                            fieldsNeeds.map((need, index) => (
                                                <span
                                                    key={need.id}
                                                    className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                                >
                                                    <span>{need.name}</span>
                                                    <span
                                                        onClick={() =>
                                                            removeAttribute(
                                                                skills,
                                                                index,
                                                                need.need_id,
                                                                setSelectedNeeds,
                                                                'need_id',
                                                                fieldsNeeds,
                                                                removeNeed
                                                            )
                                                        }
                                                    >
                                                        <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                    </span>
                                                </span>
                                            ))}
                                        <span
                                            onClick={() => addNeedsOrSkills('needs')}
                                            className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                        >
                                            <span>Add Need</span>
                                            <span>
                                                <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                            </span>
                                        </span>
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                    <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <Accordion className='custom-accordion' multiple={false} activeIndex={edit ? null : 0}>
                                <AccordionTab
                                    key='capabilities-tab'
                                    className='custom-accordion-tab-no-padding'
                                    header={
                                        <>
                                            <span className='text-lg font-bold text-theme1-primary'>Skills</span>
                                            <Badge value={fieldsSkills?.length} className='ns-badge'></Badge>
                                        </>
                                    }
                                >
                                    <div className='flex items-center flex-wrap'>
                                        {fieldsSkills.length > 0 && (
                                            <>
                                                {fieldsSkills.map((skill, index) => {
                                                    return (
                                                        <span
                                                            key={skill.id}
                                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                                        >
                                                            <span>{skill.name}</span>{' '}
                                                            <span
                                                                onClick={() =>
                                                                    removeAttribute(
                                                                        skills,
                                                                        index,
                                                                        skill.skill_id,
                                                                        setSelectedSkills,
                                                                        'skill_id',
                                                                        fieldsSkills,
                                                                        removeSkill
                                                                    )
                                                                }
                                                            >
                                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                            </span>
                                                        </span>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <span
                                            onClick={() => addNeedsOrSkills('skills')}
                                            className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                        >
                                            <span>Add Skill</span>{' '}
                                            <span>
                                                <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                            </span>
                                        </span>
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </BaseCard>
                </BaseCard>
            </div>
            <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-end py-4'>
                        <div className='text-theme1-primary font-bold' onClick={() => closeNeedsAndSkills()}>
                            Close
                        </div>
                    </div>
                    <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>
                        {needsOrSkills === 'needs' ? 'Needs' : 'Skills'}
                    </div>
                    {needsOrSkills === 'needs' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='needs'
                                        label='Select Needs'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedNeeds}
                                        setValue={setSelectedNeeds}
                                        options={skills}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose needs'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skills,
                                                'needs',
                                                value,
                                                'need_id',
                                                appendNeed,
                                                removeNeed,
                                                fieldsNeeds,
                                                setSelectedNeeds
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                <>
                                    {fieldsNeeds.length > 0 &&
                                        fieldsNeeds.map((need, index) => {
                                            return (
                                                <span
                                                    key={need.id}
                                                    className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    <span>{need.name}</span>{' '}
                                                    <span
                                                        onClick={() =>
                                                            removeAttribute(
                                                                skills,
                                                                index,
                                                                need.need_id,
                                                                setSelectedNeeds,
                                                                'need_id',
                                                                fieldsNeeds,
                                                                removeNeed
                                                            )
                                                        }
                                                    >
                                                        <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                    </span>
                                                </span>
                                            );
                                        })}
                                </>
                            </div>
                        </div>
                    )}
                    {needsOrSkills === 'skills' && (
                        <div>
                            <div className='mb-4'>
                                <div className='card flex justify-content-center'>
                                    <BaseTreeSelect
                                        name='skills'
                                        label='Select Skills'
                                        inputClassName='md:w-20rem w-full'
                                        value={selectedSkills}
                                        setValue={setSelectedSkills}
                                        options={skills}
                                        selectionMode='checkbox'
                                        display='chip'
                                        placeholder='Choose skills'
                                        showClear={false}
                                        clearErrors={clearErrors}
                                        filter={true}
                                        filterMode={'strict'}
                                        filterDelay={10}
                                        onSelect={(value) =>
                                            addTreeSelection(
                                                skills,
                                                'skills',
                                                value,
                                                'skill_id',
                                                appendSkill,
                                                removeSkill,
                                                fieldsSkills,
                                                setSelectedSkills
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className='flex items-center flex-wrap overflow-y-scroll'>
                                <>
                                    {fieldsSkills.length > 0 &&
                                        fieldsSkills.map((skill, index) => {
                                            return (
                                                <span
                                                    key={skill.id}
                                                    className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                >
                                                    <span>{skill.name}</span>{' '}
                                                    <span
                                                        onClick={() =>
                                                            removeAttribute(
                                                                skills,
                                                                index,
                                                                skill.skill_id,
                                                                setSelectedSkills,
                                                                'skill_id',
                                                                fieldsSkills,
                                                                removeSkill
                                                            )
                                                        }
                                                    >
                                                        <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                    </span>
                                                </span>
                                            );
                                        })}
                                </>
                            </div>
                        </div>
                    )}
                </div>
            </BaseOverlay>

            {!!userPermissions[PERMISSION_FILES]?.read && (
                <AssociateFiles
                    fields={fieldsFiles}
                    append={appendFile}
                    remove={removeFile}
                    showSearch={showFileSearch}
                    setShowSearch={setShowFileSearch}
                />
            )}

            <Notes
                fields={fieldsNotes}
                control={control}
                append={appendNote}
                remove={removeNote}
                clearErrors={clearErrors}
            />

            <div className='max-w-3xl w-full mx-auto px-8'>
                <div className='mb-6'>
                    <div className='grid grid-cols-2 gap-4'>
                        <BaseButton
                            id='btn-submit'
                            className='btn-primary'
                            onClick={edit ? handleSubmit(sendEditContact) : handleSubmit(sendAddContact)}
                            isLoading={edit ? isLoadingEdit : isLoading}
                            textValue='Save'
                            disabled={!formValues.first_name && !formValues.last_name}
                        />

                        <BaseButton
                            id='btn-cancel'
                            className='btn-primary-outline'
                            onClick={() => navigate('/contacts')}
                            textValue='Cancel'
                        />
                    </div>
                </div>
            </div>

            {!!userPermissions[PERMISSION_CONTACTS]?.delete &&
                edit &&
                contact &&
                canUpdateOrDelete(user, contact, PERMISSION_CONTACTS, 'delete') && (
                    <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mb-6'>
                        <div>
                            <div className='flex flex-col justify-center items-center'>
                                <BaseButton
                                    className='btn-red max-w-96'
                                    onClick={handleDeleteConfirmation}
                                    textValue={'Delete'}
                                />
                            </div>
                        </div>
                    </div>
                )}

            {deleteVisible && (
                <ContactDeleteConfirmation
                    deleteVisible={deleteVisible}
                    setDeleteVisible={setDeleteVisible}
                    contact={contact}
                />
            )}
        </AuthLayout>
    );
};

export default AddEditContact;
